/**
 * AdminMailView - Full Webmail Interface for Admin
 * Features: Inbox, Sent, Drafts, Compose, Search, Attachments
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowLeft,
  Mail,
  Send,
  FileText,
  Trash2,
  Inbox,
  Search,
  RefreshCw,
  Paperclip,
  Star,
  StarOff,
  Reply,
  Forward,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  X,
  Plus,
  Loader,
  AlertCircle,
  CheckCircle,
  FolderOpen,
  Archive,
  Menu
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../Toast';
import DOMPurify from 'dompurify';
import { logApiCall, logError } from '../../lib/adminErrorLog';
import { SidePanel } from '../ui/side-panel';
import { ExpandableScreen, ExpandableScreenContent } from '../ui/expandable-screen';

// Types
interface MailFolder {
  folderId: string;
  folderName: string;
  folderPath: string;
  unreadCount: number;
  messageCount: number;
  folderType: string;
}

interface MailMessage {
  messageId: string;
  folderId: string;
  from: string;
  fromAddress: string;
  to: string;
  toAddress: string;
  cc?: string;
  bcc?: string;
  subject: string;
  receivedTime: number;
  sentDateInGMT?: number;
  hasAttachment: boolean;
  isRead: boolean;
  isStarred: boolean;
  summary?: string;
  size?: number;
}

interface MailMessageFull extends MailMessage {
  content: string;
  htmlContent?: string;
  attachments?: MailAttachment[];
}

interface MailAttachment {
  attachmentId: string;
  attachmentName: string;
  attachmentSize: number;
  contentType: string;
}

interface ComposeData {
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  content: string;
  inReplyTo?: string;
  isReply?: boolean;
  isForward?: boolean;
}

interface AdminMailViewProps {
  onBack: () => void;
}

// Image attachments previewed inline when a message is opened. Past a handful
// this is a lot of downloading for photos the reader may never scroll to.
const MAX_INLINE_PREVIEWS = 8;

const IMAGE_EXTENSIONS = /\.(jpe?g|png|gif|webp|bmp|svg|heic|heif|avif|tiff?)$/i;

/**
 * Whether an attachment should be shown as a picture.
 *
 * The declared type alone is not enough: Zoho reports ordinary photo
 * attachments as `application/octet-stream` (seen on production with four
 * JPEGs on one message), which would leave a message full of photos showing
 * nothing but filenames. The API now repairs the type from the filename, and
 * this checks the filename too so a preview never depends on that alone.
 */
function isImageAttachment(attachment: MailAttachment): boolean {
  return attachment.contentType.startsWith('image/')
    || IMAGE_EXTENSIONS.test(attachment.attachmentName);
}

// Cache for folders (5 minutes)
let foldersCache: { data: MailFolder[]; timestamp: number } | null = null;
const CACHE_DURATION = 5 * 60 * 1000;

export default function AdminMailView({ onBack }: AdminMailViewProps) {
  const { user } = useAuth();
  const { success, error: showError } = useToast();

  // State
  const [folders, setFolders] = useState<MailFolder[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<MailFolder | null>(null);
  const [messages, setMessages] = useState<MailMessage[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<MailMessageFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [composeData, setComposeData] = useState<ComposeData>({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    content: ''
  });
  const [sending, setSending] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const [showFolderDrawer, setShowFolderDrawer] = useState(false);
  const [mobileView, setMobileView] = useState<'list' | 'detail'>('list');

  // Body with cid: references already swapped for displayable data URLs, and
  // object URLs for image attachments so they can be previewed in place.
  const [resolvedHtml, setResolvedHtml] = useState<string | null>(null);
  const [attachmentPreviews, setAttachmentPreviews] = useState<Record<string, string>>({});
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const PAGE_SIZE = 25;
  const contentRef = useRef<HTMLDivElement>(null);
  // Object URLs currently handed to <img>; revoked when the message changes.
  const previewUrlsRef = useRef<string[]>([]);
  // The message the reader is actually looking at. Image loading is async and a
  // click through the list can outrun it, so every result is checked against
  // this before it lands — otherwise one message's photos appear under another.
  const openMessageRef = useRef<string | null>(null);

  // API helper
  const mailApi = useCallback(async (
    endpoint: string,
    options: RequestInit = {}
  ) => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Admin-Email': user?.email || ''
    };

    const response = await fetch(`/api/mail/${endpoint}`, {
      ...options,
      headers: { ...headers, ...options.headers }
    });

    if (!response.ok) {
      const rawText = await response.text().catch(() => '');
      let parsed: any = {};
      try { parsed = JSON.parse(rawText); } catch {}
      const detail = parsed.error || parsed.message || rawText.slice(0, 500) || '(no body)';
      logApiCall(options.method || 'GET', `/api/mail/${endpoint}`, response.status, detail);

      // A response with no JSON body (Vercel's own routing error page, a gateway
      // timeout, etc.) never reached our handler — show a short, friendly message
      // instead of dumping the raw HTML/text error page in the UI.
      const msg = parsed.error || parsed.message
        ? `HTTP ${response.status} — /api/mail/${endpoint}\n${detail}`
        : `Couldn't reach the mail server (HTTP ${response.status}). Please try again.`;
      throw new Error(msg);
    }
    logApiCall(options.method || 'GET', `/api/mail/${endpoint}`, response.status, 'ok');

    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      return response.json();
    }

    return response;
  }, [user?.email]);

  // Load folders
  const loadFolders = useCallback(async (force = false) => {
    // Check cache
    if (!force && foldersCache && Date.now() - foldersCache.timestamp < CACHE_DURATION) {
      setFolders(foldersCache.data);
      if (!selectedFolder && foldersCache.data.length > 0) {
        const inbox = foldersCache.data.find(f =>
          f.folderType === 'Inbox' || f.folderName.toLowerCase() === 'inbox'
        );
        setSelectedFolder(inbox || foldersCache.data[0]);
      }
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await mailApi('folders');
      const foldersList = data.folders || [];

      // Update cache
      foldersCache = { data: foldersList, timestamp: Date.now() };

      setFolders(foldersList);

      // Auto-select inbox
      if (!selectedFolder && foldersList.length > 0) {
        const inbox = foldersList.find((f: MailFolder) =>
          f.folderType === 'Inbox' || f.folderName.toLowerCase() === 'inbox'
        );
        setSelectedFolder(inbox || foldersList[0]);
      }
    } catch (err: any) {
      console.warn('[Mail] Failed to load folders:', err);
      setError(err.message || 'Failed to load folders');
    } finally {
      setLoading(false);
    }
  }, [mailApi, selectedFolder]);

  // Load messages
  const loadMessages = useCallback(async (folderId: string, pageNum = 0, append = false) => {
    try {
      setLoadingMessages(true);
      setError(null);

      const start = pageNum * PAGE_SIZE;
      const data = await mailApi(`messages?folderId=${folderId}&limit=${PAGE_SIZE}&start=${start}`);

      const newMessages = data.messages || [];
      setMessages(prev => append ? [...prev, ...newMessages] : newMessages);
      setHasMore(newMessages.length === PAGE_SIZE);
      setPage(pageNum);
    } catch (err: any) {
      console.error('Failed to load messages:', err);
      setError(err.message || 'Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  }, [mailApi]);

  // Fetch one attachment's bytes. folderId and the filename both matter: Zoho
  // only serves inline parts from the folder-scoped path, and the name lets the
  // API recover a part straight from the message source when the id 404s.
  const fetchAttachmentBlob = useCallback(async (
    messageId: string,
    folderId: string | undefined,
    attachment: MailAttachment
  ): Promise<Blob> => {
    const params = new URLSearchParams({
      messageId,
      attachmentId: attachment.attachmentId,
      name: attachment.attachmentName
    });
    if (folderId) params.set('folderId', folderId);

    const response = await fetch(`/api/mail/attachment?${params.toString()}`, {
      headers: { 'X-Admin-Email': user?.email || '' }
    });
    if (!response.ok) {
      const detail = await response.json().catch(() => null);
      throw new Error(detail?.error || `Download failed (${response.status})`);
    }
    return response.blob();
  }, [user?.email]);

  /**
   * Make a message's images visible.
   *
   * Embedded photos arrive as `<img src="cid:...">`, which no browser can
   * resolve on its own — the bytes live in the MIME message, not at a URL. The
   * API hands them back keyed by Content-ID and they are swapped in here.
   * Image attachments get an inline preview at the same time, so a photo sent
   * as a file is visible without downloading it first.
   */
  const resolveInlineImages = useCallback(async (message: MailMessageFull) => {
    const html = message.htmlContent || '';
    const isStillOpen = () => openMessageRef.current === message.messageId;

    if (/src\s*=\s*["']?cid:/i.test(html)) {
      try {
        const params = new URLSearchParams({ messageId: message.messageId });
        if (message.folderId) params.set('folderId', message.folderId);
        const data = await mailApi(`inline?${params.toString()}`);

        const byContentId = new Map<string, string>(
          (data.images || []).map((img: { contentId: string; contentType: string; content: string }) => [
            img.contentId,
            `data:${img.contentType};base64,${img.content}`
          ])
        );

        if (byContentId.size > 0 && isStillOpen()) {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          doc.querySelectorAll('img[src^="cid:"], img[src^="CID:"]').forEach(img => {
            const cid = (img.getAttribute('src') || '').slice(4).replace(/^<|>$/g, '');
            const replacement = byContentId.get(cid) || byContentId.get(decodeURIComponent(cid));
            if (replacement) img.setAttribute('src', replacement);
          });
          setResolvedHtml(doc.body.innerHTML);
        }
      } catch (err) {
        // A body that renders with broken images still beats no body at all.
        console.error('Failed to load inline images:', err);
      }
    }

    const images = (message.attachments || []).filter(isImageAttachment);
    for (const attachment of images.slice(0, MAX_INLINE_PREVIEWS)) {
      if (!isStillOpen()) return;
      try {
        const blob = await fetchAttachmentBlob(message.messageId, message.folderId, attachment);
        if (!isStillOpen()) return;
        const url = URL.createObjectURL(blob);
        previewUrlsRef.current.push(url);
        setAttachmentPreviews(prev => ({ ...prev, [attachment.attachmentId]: url }));
      } catch (err) {
        console.error(`Preview failed for ${attachment.attachmentName}:`, err);
      }
    }
  }, [mailApi, fetchAttachmentBlob]);

  // Load single message
  const loadMessage = useCallback(async (messageId: string, folderId?: string) => {
    try {
      setLoadingMessage(true);
      setError(null);

      const qs = folderId ? `&folderId=${encodeURIComponent(folderId)}` : '';
      const data = await mailApi(`message?id=${encodeURIComponent(messageId)}${qs}`);

      // Drop the previous message's images before showing the new one.
      openMessageRef.current = messageId;
      previewUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
      previewUrlsRef.current = [];
      setAttachmentPreviews({});
      setResolvedHtml(null);
      setSelectedMessage(data.message);

      resolveInlineImages(data.message).catch(err =>
        console.error('Inline image resolution failed:', err)
      );

      // Mark as read
      if (!data.message.isRead) {
        mailApi('read', {
          method: 'PUT',
          body: JSON.stringify({ messageId, isRead: true })
        }).catch(console.error);

        // Update local state
        setMessages(prev => prev.map(m =>
          m.messageId === messageId ? { ...m, isRead: true } : m
        ));
      }
    } catch (err: any) {
      console.error('Failed to load message:', err);
      logError(err.message || 'Failed to load message');
      setError(err.message || 'Failed to load message');
    } finally {
      setLoadingMessage(false);
    }
  }, [mailApi, resolveInlineImages]);

  // Search
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      if (selectedFolder) {
        loadMessages(selectedFolder.folderId);
      }
      return;
    }

    try {
      setIsSearching(true);
      setError(null);

      const params = new URLSearchParams({ q: searchQuery });
      if (selectedFolder) {
        params.append('folderId', selectedFolder.folderId);
      }

      const data = await mailApi(`search?${params.toString()}`);
      setMessages(data.messages || []);
      setHasMore(false);
    } catch (err: any) {
      console.error('Search failed:', err);
      setError(err.message || 'Search failed');
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery, selectedFolder, mailApi, loadMessages]);

  // Send email
  const handleSend = useCallback(async () => {
    if (!composeData.to.trim() || !composeData.subject.trim()) {
      showError('To and Subject are required');
      return;
    }

    try {
      setSending(true);

      await mailApi('send', {
        method: 'POST',
        body: JSON.stringify({
          to: composeData.to,
          cc: composeData.cc || undefined,
          bcc: composeData.bcc || undefined,
          subject: composeData.subject,
          content: composeData.content,
          isHtml: true,
          inReplyTo: composeData.inReplyTo
        })
      });

      success('Email sent successfully');
      setShowCompose(false);
      setComposeData({ to: '', cc: '', bcc: '', subject: '', content: '' });

      // Refresh sent folder if viewing it
      const sentFolder = folders.find(f =>
        f.folderType === 'Sent' || f.folderName.toLowerCase() === 'sent'
      );
      if (selectedFolder && selectedFolder.folderId === sentFolder?.folderId) {
        loadMessages(selectedFolder.folderId);
      }
    } catch (err: any) {
      console.error('Failed to send:', err);
      showError(err.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  }, [composeData, mailApi, success, showError, folders, selectedFolder, loadMessages]);

  // Save draft
  const handleSaveDraft = useCallback(async () => {
    try {
      await mailApi('draft', {
        method: 'POST',
        body: JSON.stringify({
          to: composeData.to,
          cc: composeData.cc || undefined,
          bcc: composeData.bcc || undefined,
          subject: composeData.subject,
          content: composeData.content,
          isHtml: true
        })
      });

      success('Draft saved');
    } catch (err: any) {
      console.error('Failed to save draft:', err);
      showError(err.message || 'Failed to save draft');
    }
  }, [composeData, mailApi, success, showError]);

  // Delete message
  const handleDelete = useCallback(async (messageId: string) => {
    try {
      await mailApi(`message?id=${encodeURIComponent(messageId)}`, { method: 'DELETE' });
      success('Message deleted');

      // Remove from list
      setMessages(prev => prev.filter(m => m.messageId !== messageId));
      if (selectedMessage?.messageId === messageId) {
        setSelectedMessage(null);
      }
    } catch (err: any) {
      console.error('Failed to delete:', err);
      showError(err.message || 'Failed to delete message');
    }
  }, [mailApi, success, showError, selectedMessage]);

  // Move message
  const handleMove = useCallback(async (messageId: string, folderId: string) => {
    try {
      await mailApi('move', {
        method: 'PUT',
        body: JSON.stringify({ messageId, folderId })
      });
      success('Message moved');

      // Remove from current list
      setMessages(prev => prev.filter(m => m.messageId !== messageId));
      if (selectedMessage?.messageId === messageId) {
        setSelectedMessage(null);
      }
      setShowMoveMenu(false);
    } catch (err: any) {
      console.error('Failed to move:', err);
      showError(err.message || 'Failed to move message');
    }
  }, [mailApi, success, showError, selectedMessage]);

  // Toggle star
  const handleToggleStar = useCallback(async (messageId: string, currentState: boolean) => {
    try {
      await mailApi('star', {
        method: 'PUT',
        body: JSON.stringify({ messageId, isStarred: !currentState })
      });

      setMessages(prev => prev.map(m =>
        m.messageId === messageId ? { ...m, isStarred: !currentState } : m
      ));

      if (selectedMessage?.messageId === messageId) {
        setSelectedMessage(prev => prev ? { ...prev, isStarred: !currentState } : null);
      }
    } catch (err: any) {
      console.error('Failed to toggle star:', err);
    }
  }, [mailApi, selectedMessage]);

  // Reply
  const handleReply = useCallback(() => {
    if (!selectedMessage) return;

    setComposeData({
      to: selectedMessage.fromAddress,
      cc: '',
      bcc: '',
      subject: `Re: ${selectedMessage.subject.replace(/^Re:\s*/i, '')}`,
      content: `\n\n---\nOn ${new Date(selectedMessage.receivedTime).toLocaleString()}, ${selectedMessage.from} wrote:\n> ${(selectedMessage.content || '').replace(/\n/g, '\n> ')}`,
      inReplyTo: selectedMessage.messageId,
      isReply: true
    });
    setShowCompose(true);
  }, [selectedMessage]);

  // Forward
  const handleForward = useCallback(() => {
    if (!selectedMessage) return;

    setComposeData({
      to: '',
      cc: '',
      bcc: '',
      subject: `Fwd: ${selectedMessage.subject.replace(/^Fwd:\s*/i, '')}`,
      content: `\n\n---\nForwarded message:\nFrom: ${selectedMessage.from}\nDate: ${new Date(selectedMessage.receivedTime).toLocaleString()}\nSubject: ${selectedMessage.subject}\n\n${selectedMessage.content || ''}`,
      isForward: true
    });
    setShowCompose(true);
  }, [selectedMessage]);

  // Download attachment
  const handleDownloadAttachment = useCallback(async (
    messageId: string,
    folderId: string | undefined,
    attachment: MailAttachment
  ) => {
    setDownloadingId(attachment.attachmentId);
    try {
      const blob = await fetchAttachmentBlob(messageId, folderId, attachment);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.attachmentName;
      // Safari ignores a click on an element that is not in the document.
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoking immediately can cancel the download in some browsers.
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err: any) {
      console.error('Download failed:', err);
      showError(err.message || 'Failed to download attachment');
    } finally {
      setDownloadingId(null);
    }
  }, [fetchAttachmentBlob, showError]);

  // Effects
  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  // Release every object URL handed to an <img> when the view goes away.
  useEffect(() => () => {
    previewUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    previewUrlsRef.current = [];
  }, []);

  useEffect(() => {
    if (selectedFolder) {
      setSelectedMessage(null);
      setSearchQuery('');
      setMobileView('list');
      setShowFolderDrawer(false);
      loadMessages(selectedFolder.folderId);
    }
  }, [selectedFolder, loadMessages]);

  // Format date
  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Format size
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Get folder icon
  const getFolderIcon = (folder: MailFolder) => {
    const name = folder.folderName.toLowerCase();
    const type = folder.folderType.toLowerCase();

    if (type === 'inbox' || name === 'inbox') return <Inbox className="w-4 h-4" />;
    if (type === 'sent' || name === 'sent') return <Send className="w-4 h-4" />;
    if (type === 'drafts' || name === 'drafts') return <FileText className="w-4 h-4" />;
    if (type === 'trash' || name === 'trash') return <Trash2 className="w-4 h-4" />;
    if (type === 'archive' || name === 'archive') return <Archive className="w-4 h-4" />;
    return <FolderOpen className="w-4 h-4" />;
  };

  // Folder list — shared between the desktop sidebar and the mobile drawer
  const renderFolderList = () => (
    <>
      {folders.map(folder => (
        <button data-tour="mail-folder"
          key={folder.folderId}
          onClick={() => setSelectedFolder(folder)}
          className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition ${selectedFolder?.folderId === folder.folderId
            ? 'bg-white/10 text-white'
            : 'text-white/60 hover:text-white hover:bg-white/5'
            }`}
        >
          {getFolderIcon(folder)}
          <span className="flex-1 truncate">{folder.folderName}</span>
          {folder.unreadCount > 0 && (
            <span className="text-xs bg-white/20 px-1.5 py-0.5 rounded-full">
              {folder.unreadCount}
            </span>
          )}
        </button>
      ))}

      <div className="pt-4 border-none mt-4">
        <button
          onClick={() => loadFolders(true)}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-white/40 hover:text-white transition"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>
    </>
  );

  // Render loading state
  if (loading) {
    return (
      <div className="flex flex-col h-full items-center justify-center gap-3">
        <Loader className="w-6 h-6 animate-spin text-white/60" />
        <span className="text-white/60 text-sm">Loading mail...</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Top bar — folder menu (mobile), search, compose */}
      <div className="shrink-0 flex items-center gap-2 pb-3">
        <button data-tour="mail-folder-toggle"
          onClick={() => setShowFolderDrawer(true)}
          className="md:hidden p-2 text-white/60 hover:text-white hover:bg-white/10 transition shrink-0"
          aria-label="Folders"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div data-tour="mail-search" className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Search messages..."
            className="w-full bg-white/5 border-none pl-9 pr-3 py-2 text-sm text-white placeholder-white/40 focus:outline-none"
          />
        </div>
        <button data-tour="mail-compose"
          onClick={() => {
            setComposeData({ to: '', cc: '', bcc: '', subject: '', content: '' });
            setShowCompose(true);
          }}
          className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-white text-black font-semibold hover:bg-white/90 transition rounded-none text-sm shrink-0"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Compose</span>
        </button>
      </div>

      {/* Error display */}
      {error && (
        <div className="shrink-0 bg-red-500/10 border-l-2 border-red-500 p-3 mb-2">
          <div className="flex items-start gap-2 mb-1">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
            <span className="text-red-400 text-xs font-semibold uppercase tracking-wider">Error</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300 flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
          <pre className="text-red-300 text-xs font-mono whitespace-pre-wrap break-all leading-relaxed pl-6">{error}</pre>
        </div>
      )}

      {/* Main layout — fills remaining height */}
      <div className="flex-1 min-h-0 bg-black/50 border-none flex overflow-hidden">
        {/* Folder sidebar — desktop only; mobile uses the drawer opened from the top bar */}
        <div className="hidden md:block w-48 border-none p-3 space-y-1 shrink-0">
          <div className="text-xs text-white/40 uppercase tracking-wider mb-3 px-2">Folders</div>
          {renderFolderList()}
        </div>

        {/* Message list — full width on mobile until a message is opened */}
        <div className={`w-full md:w-80 border-none flex-col shrink-0 ${mobileView === 'detail' ? 'hidden md:flex' : 'flex'}`}>
          {/* Messages */}
          <div className="flex-1 overflow-auto">
            {loadingMessages ? (
              <div className="p-8 text-center">
                <Loader className="w-5 h-5 animate-spin text-white/40 mx-auto" />
              </div>
            ) : messages.length === 0 ? (
              <div className="p-8 text-center text-white/40 text-sm">
                No messages
              </div>
            ) : (
              <>
                {messages.map(msg => (
                  <div data-tour="mail-message"
                    key={msg.messageId}
                    onClick={() => { loadMessage(msg.messageId, msg.folderId); setMobileView('detail'); }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && (loadMessage(msg.messageId, msg.folderId), setMobileView('detail'))}
                    className={`w-full p-3 text-left cursor-pointer transition ${selectedMessage?.messageId === msg.messageId
                      ? 'bg-white/10'
                      : 'hover:bg-white/5'
                      } ${!msg.isRead ? 'bg-white/[0.02]' : ''}`}
                  >
                    <div className="flex items-start gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleStar(msg.messageId, msg.isStarred);
                        }}
                        className="mt-0.5"
                      >
                        {msg.isStarred ? (
                          <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        ) : (
                          <StarOff className="w-4 h-4 text-white/20 hover:text-white/40" />
                        )}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-sm truncate ${!msg.isRead ? 'font-semibold text-white' : 'text-white/80'}`}>
                            {msg.from || msg.fromAddress}
                          </span>
                          <span className="text-xs text-white/40 shrink-0">
                            {formatDate(msg.receivedTime)}
                          </span>
                        </div>
                        <div className={`text-sm truncate ${!msg.isRead ? 'font-medium text-white/90' : 'text-white/60'}`}>
                          {msg.subject}
                        </div>
                        {msg.summary && (
                          <div className="text-xs text-white/40 truncate mt-1">
                            {msg.summary}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          {msg.hasAttachment && (
                            <Paperclip className="w-3 h-3 text-white/40" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Pagination */}
                {(hasMore || page > 0) && (
                  <div className="p-3 flex items-center justify-center gap-2">
                    <button
                      onClick={() => selectedFolder && loadMessages(selectedFolder.folderId, page - 1)}
                      disabled={page === 0}
                      className="p-2 text-white/40 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-xs text-white/40">Page {page + 1}</span>
                    <button
                      onClick={() => selectedFolder && loadMessages(selectedFolder.folderId, page + 1)}
                      disabled={!hasMore}
                      className="p-2 text-white/40 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Message content — full width on mobile once a message is opened */}
        <div className={`flex-1 flex-col min-w-0 ${mobileView === 'list' ? 'hidden md:flex' : 'flex'}`}>
          {loadingMessage ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader className="w-6 h-6 animate-spin text-white/40" />
            </div>
          ) : selectedMessage ? (
            <>
              {/* Message header */}
              <div className="p-4 border-none">
                <button
                  onClick={() => setMobileView('list')}
                  className="md:hidden flex items-center gap-1 text-white/60 hover:text-white text-xs uppercase tracking-wider font-bold mb-3 transition"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {selectedFolder?.folderName || 'Inbox'}
                </button>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-semibold text-white truncate">
                      {selectedMessage.subject}
                    </h2>
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-white/40 w-12">From:</span>
                        <span className="text-white">{selectedMessage.from || selectedMessage.fromAddress}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-white/40 w-12">To:</span>
                        <span className="text-white/70">{selectedMessage.to || selectedMessage.toAddress}</span>
                      </div>
                      {selectedMessage.cc && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-white/40 w-12">CC:</span>
                          <span className="text-white/70">{selectedMessage.cc}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-white/40 w-12">Date:</span>
                        <span className="text-white/70">
                          {new Date(selectedMessage.receivedTime).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button data-tour="mail-reply"
                      onClick={handleReply}
                      className="p-2 text-white/60 hover:text-white hover:bg-white/10 transition"
                      title="Reply"
                    >
                      <Reply className="w-4 h-4" />
                    </button>
                    <button data-tour="mail-forward"
                      onClick={handleForward}
                      className="p-2 text-white/60 hover:text-white hover:bg-white/10 transition"
                      title="Forward"
                    >
                      <Forward className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleToggleStar(selectedMessage.messageId, selectedMessage.isStarred)}
                      className="p-2 text-white/60 hover:text-white hover:bg-white/10 transition"
                      title={selectedMessage.isStarred ? 'Unstar' : 'Star'}
                    >
                      {selectedMessage.isStarred ? (
                        <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                      ) : (
                        <Star className="w-4 h-4" />
                      )}
                    </button>
                    <div className="relative">
                      <button
                        onClick={() => setShowMoveMenu(!showMoveMenu)}
                        className="p-2 text-white/60 hover:text-white hover:bg-white/10 transition"
                        title="Move to folder"
                      >
                        <FolderOpen className="w-4 h-4" />
                      </button>
                      {showMoveMenu && (
                        <div className="absolute right-0 top-full mt-1 bg-black border-none shadow-xl z-50 min-w-[150px]">
                          {folders.filter(f => f.folderId !== selectedFolder?.folderId).map(folder => (
                            <button
                              key={folder.folderId}
                              onClick={() => handleMove(selectedMessage.messageId, folder.folderId)}
                              className="w-full px-3 py-2 text-left text-sm text-white/70 hover:text-white hover:bg-white/10 flex items-center gap-2"
                            >
                              {getFolderIcon(folder)}
                              {folder.folderName}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        if (confirm('Delete this message?')) {
                          handleDelete(selectedMessage.messageId);
                        }
                      }}
                      className="p-2 text-red-400/60 hover:text-red-400 hover:bg-red-400/10 transition"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Attachments */}
              {selectedMessage.attachments && selectedMessage.attachments.length > 0 && (
                <div className="px-4 py-3 border-none bg-white/[0.02]">
                  <div className="text-xs text-white/40 mb-2">
                    Attachments ({selectedMessage.attachments.length})
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedMessage.attachments.map(att => {
                      const preview = attachmentPreviews[att.attachmentId];
                      const isDownloading = downloadingId === att.attachmentId;

                      return (
                        <button
                          key={att.attachmentId}
                          onClick={() => handleDownloadAttachment(
                            selectedMessage.messageId,
                            selectedMessage.folderId,
                            att
                          )}
                          disabled={isDownloading}
                          className="flex flex-col gap-2 p-2 bg-white/5 border-none hover:bg-white/10 transition text-sm text-left disabled:opacity-50"
                          title={`Download ${att.attachmentName}`}
                        >
                          {preview && (
                            <img
                              src={preview}
                              alt={att.attachmentName}
                              className="max-h-40 max-w-[16rem] object-contain bg-black/40"
                            />
                          )}
                          <span className="flex items-center gap-2">
                            {isDownloading ? (
                              <Loader className="w-4 h-4 text-white/40 animate-spin" />
                            ) : (
                              <Paperclip className="w-4 h-4 text-white/40" />
                            )}
                            <span className="text-white/80">{att.attachmentName}</span>
                            <span className="text-white/40 text-xs">
                              ({formatSize(att.attachmentSize)})
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Message body */}
              <div
                ref={contentRef}
                className="flex-1 overflow-auto p-4"
              >
                {selectedMessage.htmlContent ? (
                  <div
                    className="prose prose-invert max-w-none text-white/80 [&_img]:max-w-full [&_img]:h-auto"
                    dangerouslySetInnerHTML={{
                      // resolvedHtml is the same body with cid: images swapped
                      // for data URLs; it is null until (and unless) that
                      // resolves, so the text is never held back on it.
                      __html: DOMPurify.sanitize(resolvedHtml || selectedMessage.htmlContent, {
                        ADD_TAGS: ['style'],
                        ADD_ATTR: ['target']
                      })
                    }}
                  />
                ) : (
                  <pre className="whitespace-pre-wrap text-sm text-white/80 font-sans">
                    {selectedMessage.content}
                  </pre>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-white/40">
              <div className="text-center">
                <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Select a message to read</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Folder drawer — mobile only */}
      <SidePanel isOpen={showFolderDrawer} onClose={() => setShowFolderDrawer(false)} title="Folders">
        <div className="space-y-1">
          {renderFolderList()}
        </div>
      </SidePanel>

      {/* Compose — fullscreen ExpandableScreen pattern */}
      <ExpandableScreen isOpen={showCompose} onOpenChange={setShowCompose}>
        <ExpandableScreenContent className="overflow-x-hidden">
          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            <div className="max-w-2xl mx-auto w-full px-4 sm:px-8 pt-20 pb-16">
              <div className="flex items-center gap-3 mb-8">
                <Send className="w-5 h-5 text-white/40" />
                <h2 className="text-xl font-black uppercase tracking-wide text-white">
                  {composeData.isReply ? 'Reply' : composeData.isForward ? 'Forward' : 'New Message'}
                </h2>
              </div>

              {/* Form */}
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-white/40 mb-1 block">To *</label>
                  <input
                    type="text"
                    value={composeData.to}
                    onChange={(e) => setComposeData(prev => ({ ...prev, to: e.target.value }))}
                    className="w-full bg-white/5 px-3 py-2 text-sm text-white focus:outline-none focus:bg-white/10"
                    placeholder="recipient@example.com"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-white/40 mb-1 block">CC</label>
                    <input
                      type="text"
                      value={composeData.cc}
                      onChange={(e) => setComposeData(prev => ({ ...prev, cc: e.target.value }))}
                      className="w-full bg-white/5 px-3 py-2 text-sm text-white focus:outline-none focus:bg-white/10"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-white/40 mb-1 block">BCC</label>
                    <input
                      type="text"
                      value={composeData.bcc}
                      onChange={(e) => setComposeData(prev => ({ ...prev, bcc: e.target.value }))}
                      className="w-full bg-white/5 px-3 py-2 text-sm text-white focus:outline-none focus:bg-white/10"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-white/40 mb-1 block">Subject *</label>
                  <input
                    type="text"
                    value={composeData.subject}
                    onChange={(e) => setComposeData(prev => ({ ...prev, subject: e.target.value }))}
                    className="w-full bg-white/5 px-3 py-2 text-sm text-white focus:outline-none focus:bg-white/10"
                  />
                </div>

                <div>
                  <label className="text-xs text-white/40 mb-1 block">Message</label>
                  <textarea
                    value={composeData.content}
                    onChange={(e) => setComposeData(prev => ({ ...prev, content: e.target.value }))}
                    rows={12}
                    className="w-full bg-white/5 px-3 py-2 text-sm text-white focus:outline-none focus:bg-white/10 resize-none"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between mt-6 pt-6 bg-white/[0.02] px-4 py-3">
                <button data-tour="mail-draft"
                  onClick={handleSaveDraft}
                  className="px-4 py-2 text-sm text-white/60 hover:text-white transition"
                >
                  Save Draft
                </button>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowCompose(false)}
                    className="px-4 py-2 text-sm text-white/60 hover:text-white transition"
                  >
                    Cancel
                  </button>
                  <button data-tour="mail-send"
                    onClick={handleSend}
                    disabled={sending}
                    className="flex items-center gap-2 px-4 py-2 bg-white text-black font-semibold hover:bg-white/90 transition disabled:opacity-50"
                  >
                    {sending ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    Send
                  </button>
                </div>
              </div>
            </div>
          </div>
        </ExpandableScreenContent>
      </ExpandableScreen>
    </div>
  );
}
